import { axiosInstance } from "./AxiosDefaults";
import { productReservationsRoute } from "../constants/ApiRoutes";
import { ProductReservation } from "../models/Product";

export type ApiResponse<T> = {
  status?: "SUCCESS" | "ERROR";
  message?: string;
  result?: T;
};

const toYmd = (d: Date) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export const getAllProductReservations = (params?: {
  year?: number;
  dateFrom?: Date;
  dateTo?: Date;
}): Promise<any> => {
  const q: string[] = [];
  if (typeof params?.year === "number") q.push(`year=${params.year}`);
  if (params?.dateFrom) q.push(`dateFrom=${toYmd(params.dateFrom)}`);
  if (params?.dateTo) q.push(`dateTo=${toYmd(params.dateTo)}`);

  const route = `${productReservationsRoute}${q.length ? `?${q.join("&")}` : ""}`;
  return axiosInstance.get<ApiResponse<ProductReservation[]>>(route, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

const stripEmptyId = <T extends { id?: any }>(obj: T): Omit<T, "id"> | T => {
  const copy: any = { ...obj };
  if (copy.id === "" || copy.id === null || copy.id === undefined)
    delete copy.id;
  return copy;
};

const stripEmptyUuidProps = <T extends Record<string, any>>(
  obj: T,
  keys: string[],
) => {
  const copy: any = { ...obj };
  keys.forEach((k) => {
    if (copy[k] === "" || copy[k] === null || copy[k] === undefined)
      delete copy[k];
  });
  return copy;
};

const stripNestedEmptyId = (payload: any) => {
  const copy: any = { ...payload };
  if (copy.warehouse && !copy.warehouse.id) delete copy.warehouse;
  if (copy.product && !copy.product.id) delete copy.product;
  if (copy.partner && !copy.partner.id) delete copy.partner;
  if (copy.category && !copy.category.id) delete copy.category;
  return copy;
};

export const getOneProductReservation = (id: string) => {
  if (!id) return Promise.reject(new Error("Missing reservation id"));
  return axiosInstance.get(`${productReservationsRoute}/${id}`, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const createProductReservation = (
  payload: Partial<ProductReservation>,
) => {
  let clean: any = stripEmptyId(payload as any);
  clean = stripEmptyUuidProps(clean, ["requestId", "dispositionId"]);
  clean = stripNestedEmptyId(clean);

  return axiosInstance.post(productReservationsRoute, clean, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const updateOneProductReservation = (
  id: string,
  payload: Partial<ProductReservation>,
) => {
  if (!id) return Promise.reject(new Error("Missing reservation id"));

  let clean: any = { ...payload };
  clean = stripEmptyUuidProps(clean, ["requestId", "dispositionId"]);
  clean = stripNestedEmptyId(clean);

  return axiosInstance.put(`${productReservationsRoute}/${id}`, clean, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};

export const deleteOneProductReservation = (id: string) => {
  if (!id) return Promise.reject(new Error("Missing reservation id"));
  return axiosInstance.delete(`${productReservationsRoute}/${id}`, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
};
